import { BarChart, Download, Globe, Link, Search, Shield, TrendingUp, Users } from 'lucide-react'
import { useMemo } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import { EnhancedDataTable } from '../shared/EnhancedDataTable'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { getColumnValue } from './column-mappings'
import { rowsToObjects } from './utils'

function SeoDomainAnalysisDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  // Skip schema validation - use CSV data directly like CSV Reader template tool
  const result = nodeExecution.outputs

  // Handle both array and object result formats
  const resultData = Array.isArray(result) ? result[0] : result

  // Get query context from structured result
  const domain = resultData?.query?.domain || ''
  const competitorDomains = resultData?.query?.competitorDomains || []
  const action = resultData?.query?.action || ''
  const database = resultData?.query?.database || ''
  const _recordCount = resultData?.metadata?.recordCount || 0
  const executedAt = resultData?.metadata?.executedAt || new Date().toISOString()

  // Parse CSV from structured result
  const csvData = resultData?.csvData

  // Export functions
  const exportToCsv = () => {
    if (!csvData || typeof csvData !== 'string') return

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `domain-analysis-${domain.replace(/[^a-z0-9]/gi, '-')}-${Date.now()}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Context menu actions
  const contextActions: ContextAction[] = csvData
    ? [
        {
          id: 'export-csv',
          label: 'Export CSV',
          icon: <Download className="h-3 w-3" />,
          onClick: exportToCsv,
        },
      ]
    : []

  // Determine if this action provides domain-level overview metrics
  const hasDomainOverviewMetrics = (actionType: string): boolean => {
    return ['GetDomainCompetitors'].includes(actionType)
  }

  const {
    organicKeywords,
    organicTraffic,
    authorityScore,
    backlinks,
    referringDomains,
    paidKeywords,
    paidTraffic,
    topKeywords,
    competitorScore,
  } = (() => {
    // Local CSV parser that supports semicolon delimiter and quotes
    const parseCsv = (csv: string): { headers: string[]; rows: string[][] } => {
      const lines = csv.split(/\r?\n/).filter((l) => l.trim().length > 0)
      const rows: string[][] = []
      for (const line of lines) {
        const fields: string[] = []
        let current = ''
        let inQuotes = false
        for (let i = 0; i < line.length; i++) {
          const ch = line[i]
          const next = line[i + 1]
          if (ch === '"') {
            if (inQuotes && next === '"') {
              current += '"'
              i++
            } else {
              inQuotes = !inQuotes
            }
          } else if (ch === ';' && !inQuotes) {
            fields.push(current)
            current = ''
          } else {
            current += ch
          }
        }
        fields.push(current)
        rows.push(fields)
      }
      const headers = rows.length > 0 ? rows[0].map((h) => h.trim()) : []
      const dataRows = rows.length > 1 ? rows.slice(1) : []
      return { headers, rows: dataRows }
    }

    const coerceNumber = (val: string | undefined): number => {
      if (!val) return 0
      const cleaned = val.replace(/[,\s]/g, '')
      const n = Number(cleaned)
      return Number.isFinite(n) ? n : 0
    }

    if (!csvData || typeof csvData !== 'string') {
      return {
        organicKeywords: 0,
        organicTraffic: 0,
        authorityScore: 0,
        backlinks: 0,
        referringDomains: 0,
        paidKeywords: 0,
        paidTraffic: 0,
        topKeywords: [] as string[],
        competitorScore: 0,
      }
    }

    const { headers, rows } = parseCsv(csvData)
    if (headers.length === 0 || rows.length === 0) {
      return {
        organicKeywords: 0,
        organicTraffic: 0,
        authorityScore: 0,
        backlinks: 0,
        referringDomains: 0,
        paidKeywords: 0,
        paidTraffic: 0,
        topKeywords: [] as string[],
        competitorScore: 0,
      }
    }

    // Build index map of headers (support both short codes and readable headers)
    const lowerHeaders = headers.map((h) => h.toLowerCase())
    const idx = (aliases: string[]): number => {
      for (const a of aliases) {
        const k = a.toLowerCase()
        const i = lowerHeaders.indexOf(k)
        if (i !== -1) return i
      }
      return -1
    }

    const first = rows[0]
    const organicKeywordsIdx = idx(['Or', 'organickeywords', 'organic keywords'])
    const organicTrafficIdx = idx(['Ot', 'organictraffic', 'organic traffic'])
    const authorityScoreIdx = idx(['As', 'authorityscore', 'authority score'])
    const backlinksIdx = idx(['Bk', 'backlinks'])
    const referringDomainsIdx = idx(['Rd', 'referringdomains', 'referring domains'])
    const paidKeywordsIdx = idx(['Pk', 'paidkeywords', 'paid keywords'])
    const paidTrafficIdx = idx(['Pt', 'paidtraffic', 'paid traffic'])
    const keywordIdx = idx(['Ph', 'keyword', 'term', 'query'])
    const competitorScoreIdx = idx([
      'Co',
      'competition',
      'competitive density',
      'competitorrelevance',
    ])

    const extractedTopKeywords: string[] = []
    if (keywordIdx !== -1) {
      for (const r of rows) {
        const v = (r[keywordIdx] || '').trim()
        if (v) extractedTopKeywords.push(v)
      }
    }

    return {
      organicKeywords: organicKeywordsIdx !== -1 ? coerceNumber(first[organicKeywordsIdx]) : 0,
      organicTraffic: organicTrafficIdx !== -1 ? coerceNumber(first[organicTrafficIdx]) : 0,
      authorityScore: authorityScoreIdx !== -1 ? coerceNumber(first[authorityScoreIdx]) : 0,
      backlinks: backlinksIdx !== -1 ? coerceNumber(first[backlinksIdx]) : 0,
      referringDomains: referringDomainsIdx !== -1 ? coerceNumber(first[referringDomainsIdx]) : 0,
      paidKeywords: paidKeywordsIdx !== -1 ? coerceNumber(first[paidKeywordsIdx]) : 0,
      paidTraffic: paidTrafficIdx !== -1 ? coerceNumber(first[paidTrafficIdx]) : 0,
      topKeywords: extractedTopKeywords,
      competitorScore: competitorScoreIdx !== -1 ? coerceNumber(first[competitorScoreIdx]) : 0,
    }
  })()

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    if (score >= 40) return 'text-orange-600'
    return 'text-red-600'
  }

  // Memoize expensive CSV processing to avoid re-computation on every render
  const tableData = useMemo(() => {
    if (!csvData || typeof csvData !== 'string') return []

    // Enhanced CSV parser that handles both comma and semicolon separators
    const parseFlexibleCsv = (csv: string): { headers: string[]; rows: string[][] } => {
      const lines = csv.split(/\r?\n/).filter((l) => l.trim().length > 0)
      const rows: string[][] = []

      // Detect separator by checking first line
      const firstLine = lines[0] || ''
      const separator = firstLine.includes(';') ? ';' : ','

      for (const line of lines) {
        const fields: string[] = []
        let current = ''
        let inQuotes = false
        for (let i = 0; i < line.length; i++) {
          const ch = line[i]
          const next = line[i + 1]
          if (ch === '"') {
            if (inQuotes && next === '"') {
              current += '"'
              i++
            } else {
              inQuotes = !inQuotes
            }
          } else if (ch === separator && !inQuotes) {
            fields.push(current)
            current = ''
          } else {
            current += ch
          }
        }
        fields.push(current)
        rows.push(fields)
      }
      const headers = rows.length > 0 ? rows[0].map((h) => h.trim()) : []
      const dataRows = rows.length > 1 ? rows.slice(1) : []
      return { headers, rows: dataRows }
    }

    const { headers, rows } = parseFlexibleCsv(csvData)
    if (headers.length === 0 || rows.length === 0) return []
    const objs = rowsToObjects(headers, rows)

    // Handle different action types with appropriate data formatting
    if (action === 'GetDomainAuthority') {
      // Domain Authority action only has domain and authorityScore columns
      // Note: rowsToObjects converts headers to lowercase, so 'authorityScore' becomes 'authorityscore'
      return objs.map((row) => ({
        domain: row['domain'] || '-',
        authorityScore: row['authorityscore'] || '-',
      }))
    }

    // Handle keyword-based actions (existing logic)
    // Helper functions for consistent formatting
    const formatNumber = (value: string | undefined): string => {
      if (!value) return '-'
      const cleanValue = value.replace(/[,\s]/g, '')
      const num = Number(cleanValue)
      return Number.isFinite(num) ? num.toLocaleString() : '-'
    }

    const formatCurrency = (value: string | undefined): string => {
      if (!value) return '-'
      const num = Number(value)
      return Number.isFinite(num) ? `$${num.toFixed(2)}` : '-'
    }

    const formatPercentage = (value: string | undefined): string => {
      if (!value) return '-'
      return `${value}%`
    }

    return objs.map((row) => {
      // Use unified column mapping to get values
      // Note: GetDomainOrganicKeywords may not have Kd (keywordDifficulty) unless explicitly requested
      const volume = getColumnValue(row, 'searchVolume')
      const difficulty = getColumnValue(row, 'keywordDifficulty')
      const cpc = getColumnValue(row, 'costPerClick')
      const traffic =
        getColumnValue(row, 'trafficPercent') || getColumnValue(row, 'trafficCostPercent')

      // Build result object, only including columns that have actual values
      // This ensures columns that don't exist for an action won't appear in the table
      const result: Record<string, string> = {}

      // List of cryptic column codes to exclude (we'll use formatted versions instead)
      const crypticCodes = [
        'ph',
        'nq',
        'kd',
        'cp',
        'co',
        'in',
        'po',
        'pp',
        'pd',
        'ur',
        'dn',
        'tr',
        'tc',
        'nr',
        'td',
        'rr',
        'fk',
      ]

      // Include original row data, but exclude cryptic codes (we'll add formatted versions)
      Object.keys(row).forEach((key) => {
        if (
          row[key] !== undefined &&
          row[key] !== '' &&
          !crypticCodes.includes(key.toLowerCase())
        ) {
          result[key] = row[key]
        }
      })

      // Add formatted/standardized values only if they exist (these override any cryptic codes)
      if (volume) result.volume = formatNumber(volume)
      if (difficulty) result.difficulty = formatPercentage(difficulty)
      if (cpc) result.cpc = formatCurrency(cpc)
      if (traffic) result.traffic = formatNumber(traffic)

      return result
    })
  }, [csvData, action])

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div
        className="rounded-3xl border border-gray-200/50 bg-gradient-to-br from-white/90 to-green-50/80 text-black shadow-xl backdrop-blur-[12px]"
        style={{ pointerEvents: 'none' }}
      >
        <div className="px-6 py-4">
          <div className="flex items-start gap-4">
            <div className="mt-1 flex-shrink-0">
              <div className="rounded-full bg-gradient-to-br from-green-100 to-green-200 p-3 shadow-sm">
                <Globe className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-3 flex items-center gap-3">
                <div className="rounded-md bg-gradient-to-r from-green-500 to-emerald-600 p-1">
                  <BarChart className="h-4 w-4 text-white" />
                </div>
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-base font-bold text-gray-900 text-transparent">
                  SEO Research - Domain Analysis
                </div>
                {tableData.length > 0 && (
                  <div className="ml-auto flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                    <TrendingUp className="h-3 w-3" />
                    {tableData.length} results
                  </div>
                )}
              </div>

              {/* Query Context */}
              <div className="mb-4 rounded-xl border border-green-100 bg-white/60 p-4 backdrop-blur-sm">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-green-100 p-1">
                      <Globe className="h-3 w-3 text-green-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Target Domain</span>
                      <div className="text-sm font-semibold text-gray-900">{domain}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-blue-100 p-1">
                      <BarChart className="h-3 w-3 text-blue-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Analysis Type</span>
                      <div className="text-sm font-semibold text-gray-900">{action}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-purple-100 p-1">
                      <Search className="h-3 w-3 text-purple-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Database</span>
                      <div className="text-sm font-semibold text-gray-900">
                        {database.toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-orange-100 p-1">
                      <TrendingUp className="h-3 w-3 text-orange-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Executed</span>
                      <div className="text-sm font-semibold text-gray-900">
                        {new Date(executedAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
                {competitorDomains.length > 0 && (
                  <div className="mt-3 border-t border-green-100 pt-3">
                    <div className="flex items-start gap-2">
                      <div className="rounded-md bg-indigo-100 p-1">
                        <Users className="h-3 w-3 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs font-medium text-gray-500">Competitors</span>
                        <div className="mt-1 text-sm text-gray-900">
                          {competitorDomains.slice(0, 3).join(', ')}
                          {competitorDomains.length > 3 && (
                            <span className="text-gray-500">
                              {' '}
                              +{competitorDomains.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {authorityScore > 0 && (
                <div className="mb-4 rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-gradient-to-r from-green-500 to-emerald-500 p-2">
                        <Shield className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-600">
                          Authority Score
                        </div>
                        <div className={`text-2xl font-bold ${getScoreColor(authorityScore)}`}>
                          {authorityScore}
                          <span className="text-sm font-normal text-gray-500">/100</span>
                        </div>
                      </div>
                    </div>
                    <div className="h-2 w-24 rounded-full bg-gray-200">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          authorityScore >= 80
                            ? 'bg-green-500'
                            : authorityScore >= 60
                              ? 'bg-yellow-500'
                              : authorityScore >= 40
                                ? 'bg-orange-500'
                                : 'bg-red-500'
                        }`}
                        style={{ width: `${authorityScore}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {(organicKeywords > 0 ||
                organicTraffic > 0 ||
                backlinks > 0 ||
                referringDomains > 0 ||
                paidKeywords > 0 ||
                paidTraffic > 0) && (
                <div className="mb-4 rounded-xl border border-green-100 bg-white/60 p-4 backdrop-blur-sm">
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {hasDomainOverviewMetrics(action) && organicKeywords > 0 && (
                      <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 p-3">
                        <div className="rounded-md bg-blue-500 p-2">
                          <Search className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <div className="mb-1 text-xs font-medium text-blue-600">
                            Organic Keywords
                          </div>
                          <div className="text-lg font-bold text-blue-900">
                            {formatNumber(organicKeywords)}
                          </div>
                        </div>
                      </div>
                    )}

                    {hasDomainOverviewMetrics(action) && organicTraffic > 0 && (
                      <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-green-100 p-3">
                        <div className="rounded-md bg-green-500 p-2">
                          <TrendingUp className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <div className="mb-1 text-xs font-medium text-green-600">
                            Organic Traffic
                          </div>
                          <div className="text-lg font-bold text-green-900">
                            {formatNumber(organicTraffic)}
                          </div>
                        </div>
                      </div>
                    )}

                    {backlinks > 0 && (
                      <div className="flex items-center gap-3 rounded-lg border border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100 p-3">
                        <div className="rounded-md bg-purple-500 p-2">
                          <Link className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <div className="mb-1 text-xs font-medium text-purple-600">Backlinks</div>
                          <div className="text-lg font-bold text-purple-900">
                            {formatNumber(backlinks)}
                          </div>
                        </div>
                      </div>
                    )}

                    {referringDomains > 0 && (
                      <div className="flex items-center gap-3 rounded-lg border border-orange-200 bg-gradient-to-r from-orange-50 to-orange-100 p-3">
                        <div className="rounded-md bg-orange-500 p-2">
                          <Globe className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <div className="mb-1 text-xs font-medium text-orange-600">
                            Referring Domains
                          </div>
                          <div className="text-lg font-bold text-orange-900">
                            {formatNumber(referringDomains)}
                          </div>
                        </div>
                      </div>
                    )}

                    {paidKeywords > 0 && (
                      <div className="flex items-center gap-3 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-indigo-100 p-3">
                        <div className="rounded-md bg-indigo-500 p-2">
                          <Search className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <div className="mb-1 text-xs font-medium text-indigo-600">
                            Paid Keywords
                          </div>
                          <div className="text-lg font-bold text-indigo-900">
                            {formatNumber(paidKeywords)}
                          </div>
                        </div>
                      </div>
                    )}

                    {paidTraffic > 0 && (
                      <div className="flex items-center gap-3 rounded-lg border border-pink-200 bg-gradient-to-r from-pink-50 to-pink-100 p-3">
                        <div className="rounded-md bg-pink-500 p-2">
                          <TrendingUp className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <div className="mb-1 text-xs font-medium text-pink-600">Paid Traffic</div>
                          <div className="text-lg font-bold text-pink-900">
                            {formatNumber(paidTraffic)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {competitorScore > 0 && (
                <div className="mb-3">
                  <div className="text-xs text-gray-600">
                    {hasDomainOverviewMetrics(action)
                      ? 'Competitive Positioning'
                      : 'Avg. Keyword Competition'}
                    :{' '}
                    <span className={`font-medium ${getScoreColor(competitorScore)}`}>
                      {competitorScore}%
                    </span>
                  </div>
                </div>
              )}

              {topKeywords && Array.isArray(topKeywords) && topKeywords.length > 0 && (
                <div className="mb-3">
                  <div className="mb-1 text-xs font-medium text-gray-700">Top Keywords:</div>
                  <div className="flex flex-wrap gap-1">
                    {topKeywords.slice(0, 5).map((keyword: any, index: number) => {
                      const keywordText =
                        typeof keyword === 'string'
                          ? keyword
                          : keyword.keyword || keyword.term || 'keyword'
                      return (
                        <span
                          key={index}
                          className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-700"
                        >
                          {keywordText}
                        </span>
                      )
                    })}
                    {topKeywords.length > 5 && (
                      <span className="text-xs text-gray-500">+{topKeywords.length - 5} more</span>
                    )}
                  </div>
                </div>
              )}

              {/* CSV Data Table */}
              {tableData.length > 0 && (
                <div className="mt-4" style={{ pointerEvents: 'auto' }}>
                  <EnhancedDataTable
                    data={tableData}
                    title={`Domain Data (${tableData.length} records)`}
                    maxRows={50}
                    priorityColumns={
                      action === 'GetDomainAuthority'
                        ? ['domain', 'authorityScore']
                        : ['keyword', 'position', 'volume', 'difficulty']
                    }
                    colorCodedColumns={{
                      authorityScore: 'percentage',
                      difficulty: 'difficulty',
                      volume: 'volume',
                      cpc: 'currency',
                      traffic: 'number',
                      position: 'number',
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadSeoDomainAnalysis(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function seoDomainAnalysisToMarkdown(nodeExecution: NodeExecution): string {
  // Skip schema validation - use CSV data directly like CSV Reader template tool
  const result = nodeExecution.outputs
  const domain =
    nodeExecution.inputs.domain || nodeExecution.inputs.url || nodeExecution.inputs.website || ''
  let organicKeywords = 0
  let organicTraffic = 0
  let authorityScore = 0
  let backlinks = 0
  let referringDomains = 0

  if (typeof result === 'string') {
    const parseCsv = (csv: string): { headers: string[]; rows: string[][] } => {
      const lines = csv.split(/\r?\n/).filter((l) => l.trim().length > 0)
      const rows: string[][] = []
      for (const line of lines) {
        const fields: string[] = []
        let current = ''
        let inQuotes = false
        for (let i = 0; i < line.length; i++) {
          const ch = line[i]
          const next = line[i + 1]
          if (ch === '"') {
            if (inQuotes && next === '"') {
              current += '"'
              i++
            } else {
              inQuotes = !inQuotes
            }
          } else if (ch === ';' && !inQuotes) {
            fields.push(current)
            current = ''
          } else {
            current += ch
          }
        }
        fields.push(current)
        rows.push(fields)
      }
      const headers = rows.length > 0 ? rows[0].map((h) => h.trim()) : []
      const dataRows = rows.length > 1 ? rows.slice(1) : []
      return { headers, rows: dataRows }
    }
    const coerceNumber = (val: string | undefined): number => {
      if (!val) return 0
      const cleaned = val.replace(/[,\s]/g, '')
      const n = Number(cleaned)
      return Number.isFinite(n) ? n : 0
    }
    const { headers, rows } = parseCsv(result)
    if (headers.length > 0 && rows.length > 0) {
      const lowerHeaders = headers.map((h) => h.toLowerCase())
      const idx = (aliases: string[]): number => {
        for (const a of aliases) {
          const k = a.toLowerCase()
          const i = lowerHeaders.indexOf(k)
          if (i !== -1) return i
        }
        return -1
      }
      const first = rows[0]
      const orI = idx(['Or', 'organic keywords'])
      const otI = idx(['Ot', 'organic traffic'])
      const asI = idx(['As', 'authority score'])
      const bkI = idx(['Bk', 'backlinks'])
      const rdI = idx(['Rd', 'referring domains'])
      organicKeywords = orI !== -1 ? coerceNumber(first[orI]) : 0
      organicTraffic = otI !== -1 ? coerceNumber(first[otI]) : 0
      authorityScore = asI !== -1 ? coerceNumber(first[asI]) : 0
      backlinks = bkI !== -1 ? coerceNumber(first[bkI]) : 0
      referringDomains = rdI !== -1 ? coerceNumber(first[rdI]) : 0
    }
  }

  return `## SEO Research - Domain Analysis

**Domain:** ${domain}

### Overview
${authorityScore > 0 ? `**Authority Score:** ${authorityScore}/100` : ''}
**Organic Keywords:** ${organicKeywords.toLocaleString()}
**Organic Traffic:** ${organicTraffic.toLocaleString()}
${backlinks > 0 ? `**Backlinks:** ${backlinks.toLocaleString()}` : ''}
${referringDomains > 0 ? `**Referring Domains:** ${referringDomains.toLocaleString()}` : ''}


---
`
}

export const SeoDomainAnalysisDisplay = createWorkflowToolComponent(
  SeoDomainAnalysisDisplayComponent,
  downloadSeoDomainAnalysis,
  seoDomainAnalysisToMarkdown
)
